import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { UNAUTHORIZED_EVENT, readConnection } from '../api/client';

export const sessionKey = ['auth', 'session'] as const;

export type SessionState = {
  /** True when the server accepted our cookie, or when no gate applies. */
  authenticated: boolean;
  /** True when this client talks to a remote API directly, bypassing the gate. */
  bypassed: boolean;
};

/**
 * Reports whether this browser may talk to the API.
 *
 * A client configured with its own base URL and key (Settings → API connection)
 * never goes through this server's proxy, so there is no session to check.
 */
export async function fetchSession(): Promise<SessionState> {
  if (readConnection()) return { authenticated: true, bypassed: true };

  const response = await fetch('/auth/session', {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });

  if (response.status === 401) return { authenticated: false, bypassed: false };
  if (!response.ok) throw new Error('Cannot reach the sign-in service');
  return { authenticated: true, bypassed: false };
}

export function useSession() {
  return useQuery({
    queryKey: sessionKey,
    queryFn: fetchSession,
    staleTime: 60_000,
    retry: false,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (credentials: string | { username: string; password: string }) => {
      const payload = typeof credentials === 'string' ? { password: credentials } : credentials;
      const response = await fetch('/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      });

      if (response.ok) return;

      const body = (await response.json().catch(() => null)) as { error?: string } | null;
      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After');
        throw new Error(
          retryAfter
            ? `Too many attempts. Try again in ${Math.ceil(Number(retryAfter) / 60)} min.`
            : 'Too many attempts. Try again later.',
        );
      }
      throw new Error(body?.error ?? 'Sign-in failed');
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: sessionKey });
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await fetch('/auth/logout', { method: 'POST', credentials: 'same-origin' });
    },
    onSuccess: () => {
      queryClient.clear();
      void queryClient.invalidateQueries({ queryKey: sessionKey });
    },
  });
}

/**
 * Returns to the sign-in screen as soon as the proxy rejects a request, rather
 * than leaving every panel showing "Not signed in".
 */
export function useUnauthorizedRedirect(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    const onUnauthorized = () => {
      queryClient.setQueryData(sessionKey, { authenticated: false, bypassed: false });
    };
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, [queryClient]);
}
